import { useEffect, useMemo, useRef, useState } from 'react'
import { useCapture } from './capture-context'
import { type CaptureItem, type InspectionKey, type PhotoItem, sameInspection } from './item'
import { otherVehicles, type QueueSummary, summarize, unsentFor } from './queue'

const NONE: readonly CaptureItem[] = []

/** This iPad's items of one inspection, sent or not, in capture order. */
export function useInspectionItems(key: InspectionKey | null): readonly CaptureItem[] {
  const { items } = useCapture()
  const code = key?.bookingCode
  const stage = key?.stage
  const vehicleId = key?.vehicleId
  return useMemo(() => {
    if (!items || !code || !stage || !vehicleId) return NONE
    return items
      .filter((item) => sameInspection(item, { bookingCode: code, stage, vehicleId }))
      .sort((a, b) => a.capturedAt - b.capturedAt)
  }, [items, code, stage, vehicleId])
}

/**
 * What a hand-over or a return waits for on this iPad, and (at a hand-over)
 * what was taken of another vehicle considered for it.
 */
export function useUnsentInspection(key: InspectionKey | null): {
  unsent: readonly CaptureItem[]
  elsewhere: readonly CaptureItem[]
  /** False until the store has been read: the step waits, rather than guess. */
  known: boolean
} {
  const { items } = useCapture()
  const code = key?.bookingCode
  const stage = key?.stage
  const vehicleId = key?.vehicleId
  return useMemo(() => {
    if (!items) return { unsent: NONE, elsewhere: NONE, known: false }
    if (!code || !stage || !vehicleId) return { unsent: NONE, elsewhere: NONE, known: true }
    const inspection = { bookingCode: code, stage, vehicleId }
    return {
      unsent: unsentFor(items, inspection),
      elsewhere: otherVehicles(items, inspection),
      known: true,
    }
  }, [items, code, stage, vehicleId])
}

/** Counts for the shell's indicator and the lock screen. */
export function useQueueSummary(): QueueSummary & { known: boolean } {
  const { items } = useCapture()
  return useMemo(() => ({ ...summarize(items ?? NONE), known: items !== null }), [items])
}

/**
 * A photo kept on the iPad, shown from its own memory (img-src allows blob:).
 * One address per item, made when it first shows and let go when it leaves:
 * reading the store again hands back a new Blob of the same photo, which
 * must not make the thumbnail flicker.
 */
export function usePhotoUrl(item: PhotoItem | null): string | null {
  const id = item?.id ?? null
  const bytes = useRef<ArrayBuffer | null>(null)
  bytes.current = item?.bytes ?? null
  const [shown, setShown] = useState<{ id: string; url: string } | null>(null)
  useEffect(() => {
    if (!id || !bytes.current) {
      setShown(null)
      return
    }
    const url = URL.createObjectURL(new Blob([bytes.current], { type: 'image/jpeg' }))
    setShown({ id, url })
    return () => URL.revokeObjectURL(url)
  }, [id])
  return shown?.id === id ? shown.url : null
}
